import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bell, ExternalLink, X } from "lucide-react";
import config from "../../config/Global.json";
import "./Announcements.css";

const cache = new Map();
const loadAnnouncements = (storeId, appKey) => {
  const key = `${storeId || "global"}:${appKey || "all"}`;
  if (!cache.has(key)) {
    const params = new URLSearchParams();
    if (storeId) params.set("store_id", storeId);
    if (appKey) params.set("app_key", appKey);
    cache.set(
      key,
      axios
        .get(`${config.api.host}${config.api.activeAnnouncements}?${params}`)
        .then((response) => response.data?.results || response.data || [])
        .catch(() => []),
    );
  }
  return cache.get(key);
};

const announcementVersion = (item) => item.updated_at || item.created_at || "initial";
const storageKey = (item) => `pv-announcement-${item.id}-${announcementVersion(item)}`;
const isDismissed = (item) => {
  const value = localStorage.getItem(storageKey(item));
  if (!value) return false;
  if (item.display_type !== "reminder") return true;
  return Date.now() - Number(value) < (item.reminder_hours || 24) * 3600000;
};

const useAnnouncements = (storeId, appKey) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let active = true;
    loadAnnouncements(storeId, appKey).then((data) => active && setItems(data || []));
    return () => { active = false; };
  }, [storeId, appKey]);
  return items;
};

export const AnnouncementDisplay = ({ storeId, appKey }) => {
  const items = useAnnouncements(storeId, appKey);
  const [closed, setClosed] = useState({});
  const banners = useMemo(
    () => items.filter((item) => item.display_type === "banner" && !closed[item.id] && !isDismissed(item)),
    [items, closed],
  );
  const close = (item) => {
    localStorage.setItem(storageKey(item), String(Date.now()));
    setClosed((value) => ({ ...value, [item.id]: true }));
  };
  if (!banners.length) return null;
  const banner = banners[0];
  return <section className={`pv-announcement-banner severity-${banner.severity}`}>
    <div className="pv-announcement-marquee"><span><b>{banner.title}</b> — {banner.message}</span></div>
    {banner.link_url && <a href={banner.link_url} target="_blank" rel="noreferrer">{banner.link_label}</a>}
    {banner.dismissible && <button onClick={() => close(banner)} aria-label="Close message">×</button>}
  </section>;
};

export const AppNotificationHub = ({ storeId, appKey, appTitle }) => {
  const items = useAnnouncements(storeId, appKey);
  const [open, setOpen] = useState(false);
  const [closed, setClosed] = useState({});
  const notifications = useMemo(
    () => items.filter((item) => ["popup", "reminder", "expiry"].includes(item.display_type) && !closed[item.id] && !isDismissed(item)),
    [items, closed],
  );
  const dismiss = (item) => {
    localStorage.setItem(storageKey(item), String(Date.now()));
    setClosed((value) => ({ ...value, [item.id]: true }));
  };
  useEffect(() => { if (!notifications.length) setOpen(false); }, [notifications.length]);

  return <div className={`pv-app-notification-stack ${notifications.length ? "has-unread" : ""}`}>
    {notifications.length > 0 && <button type="button" className="pv-app-notification-button" onClick={() => setOpen(true)} aria-label={`${notifications.length} notifications for ${appTitle}`}>
      <Bell size={25} fill="currentColor"/><span>{notifications.length}</span>
    </button>}
    <h5 className="mb-0 responsive-app-name">{appTitle}</h5>
    {open && <div className="pv-announcement-overlay" role="dialog" aria-modal="true" aria-label="App notifications">
      <article className="pv-notification-center">
        <header><div><span>PAISAVARA UPDATES</span><h2>{appTitle}</h2></div><button type="button" onClick={() => setOpen(false)} aria-label="Close notifications"><X/></button></header>
        <div className="pv-notification-list">{notifications.map((item) => <section key={item.id} className={`pv-notification-item severity-${item.severity}`}>
          <div className="pv-notification-icon"><Bell size={20}/></div>
          <div><small>{item.display_type === "expiry" ? "TIME-SENSITIVE" : item.display_type === "reminder" ? "REMINDER" : "NEW UPDATE"}</small><h3>{item.title}</h3><p>{item.message}</p>{item.ends_at && <time>Available until {new Date(item.ends_at).toLocaleString()}</time>}<div className="pv-notification-actions">{item.link_url && <a href={item.link_url} target="_blank" rel="noreferrer">{item.link_label || "Learn more"}<ExternalLink size={15}/></a>}{item.dismissible && <button type="button" onClick={() => dismiss(item)}>{item.display_type === "reminder" ? "Remind me later" : "Dismiss"}</button>}</div></div>
        </section>)}</div>
      </article>
    </div>}
  </div>;
};

export const AnnouncementFooterNews = ({ storeId, appKey }) => {
  const items = useAnnouncements(storeId, appKey).filter((item) => item.display_type === "footer_news");
  if (!items.length) return null;
  return <section className="pv-footer-news"><b>Paisavara news</b><div>{items.map((item) => <a key={item.id} href={item.link_url || "#"} target={item.link_url ? "_blank" : undefined} rel="noreferrer"><span>{item.title}</span><small>{item.message}</small></a>)}</div></section>;
};
